import type { ReactNode } from 'react';
import React, { useState, useEffect, createContext, useContext } from 'react';
import CloseIcon from '../components/icons/CloseIcon';
import { noop } from 'lodash';

export const BOTTOM_SHEET_ANIMATION_DURATION_MS = 300;

type Options = {
  readonly title?: string;
  readonly content?: ReactNode;
  readonly hideCloseButton?: boolean;
  /** Optional custom close handler. If provided, will be called instead of the default close action. */
  readonly onClose?: () => void;
};

interface BottomSheetStore {
  readonly isOpen: boolean;
  readonly open: (options: Options) => void;
  readonly close: () => void;
}

interface BottomSheetProviderProps {
  readonly children: ReactNode;
  readonly deviceTypeMobile: boolean;
}

const initialState: BottomSheetStore = {
  isOpen: false,
  open: noop,
  close: noop,
};

const initialOptions: Options = {
  title: '',
  content: <></>,
  hideCloseButton: false,
};

const BottomSheetContext = createContext<BottomSheetStore>(initialState);

export const useBottomSheet = () => {
  const context = useContext(BottomSheetContext);
  if (!context) {
    throw new Error('useBottomSheet must be used within BottomSheetProvider');
  }
  return context;
};

export const BottomSheetProvider: React.FC<BottomSheetProviderProps> = ({
  children,
  deviceTypeMobile,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [options, setOptions] = useState<Options>(initialOptions);

  const open = (opts: Options) => {
    setOptions(opts);
    setIsOpen(true);
  };

  const close = () => {
    // Stop the animation immediately
    setIsAnimating(false);
    // Wait for CSS animation to complete before unmounting
    setTimeout(() => {
      setIsOpen(false);
      setOptions({});
    }, BOTTOM_SHEET_ANIMATION_DURATION_MS);
  };

  useEffect(() => {
    // Trigger CSS animation after modal is mounted. Using a small delay (10ms) ensures
    // the browser renders the initial state before applying the animation class.
    if (isOpen) {
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  return (
    <BottomSheetContext.Provider value={{ isOpen, open, close }}>
      {children}
      {isOpen && (
        <div
          style={{
            ...styles.overlay,
            ...(deviceTypeMobile && styles.overlayMobile),
            opacity: isAnimating ? 1 : 0,
            transition: `opacity ${BOTTOM_SHEET_ANIMATION_DURATION_MS}ms ease-in-out`,
          }}
        >
          <div
            style={{
              ...styles.bottomSheet,
              ...(deviceTypeMobile && styles.bottomSheetMobile),
              transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
              transition: `transform ${BOTTOM_SHEET_ANIMATION_DURATION_MS}ms ease-in-out`,
            }}
          >
            <div style={styles.header}>
              {options.title && <h2 style={styles.heading}>{options.title}</h2>}
              {/* Close button uses custom onClose handler if provided, otherwise uses default close */}
              {!options.hideCloseButton && (
                <CloseIcon onClick={options.onClose || close} style={styles.closeButton} />
              )}
            </div>
            <div style={styles.content}>{options.content}</div>
          </div>
        </div>
      )}
    </BottomSheetContext.Provider>
  );
};

const styles: Styles = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '20px 20px 30px 20px',
  },
  overlayMobile: {
    position: 'fixed',
    padding: 0,
  },
  bottomSheet: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    minWidth: 660,
    maxWidth: 660,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  bottomSheetMobile: {
    borderRadius: 0,
    minWidth: 'unset',
    maxWidth: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 30,
    padding: '8px 16px 16px',
  },
  content: {
    overflow: 'auto',
    flex: 1,
  },
  heading: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
    flex: 1,
    textAlign: 'center',
  },
  closeButton: {
    fontSize: 32,
  },
};
